(() => {
  'use strict';

  const ROOT = window.Shopify?.routes?.root || '/';
  const API_ROOT = ROOT + 'apps/wishlist';
  const productCache = new Map();
  let listsCache = null;
  let activeSavePayload = null;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const numericVariantId = (value) => {
    const match = String(value ?? '').match(/(\d+)$/);
    return match ? match[1] : '';
  };

  const clampQuantity = (value) => Math.max(1, Math.min(9999, Number.parseInt(value, 10) || 1));

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  async function api(path, options = {}) {
    const init = {
      method: options.method || 'GET',
      headers: { Accept: 'application/json' },
    };

    if (options.body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }

    const response = await fetch(`${API_ROOT}${path}`, init);
    let data;
    try {
      data = await response.json();
    } catch (_) {
      throw new Error(`Saved Lists returned HTTP ${response.status}.`);
    }

    if (!response.ok || data.ok === false) {
      const code = data?.error?.code;
      if (code === 'LOGIN_REQUIRED') {
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`${window.GeightConfig?.loginUrl || (ROOT + 'account/login')}?return_url=${returnUrl}`);
      }
      throw new Error(data?.error?.message || 'Saved Lists request failed.');
    }
    return data;
  }

  async function loadLists(force = false) {
    if (!force && listsCache) return listsCache;

    const data = await api('/lists');
    listsCache = data.lists || [];
    return listsCache;
  }

  async function loadProduct(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);
    const request = fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .catch(() => null);
    productCache.set(handle, request);
    return request;
  }

  async function addItemsToCart(items) {
    if (!window.GeightPurchasing) throw new Error('Please refresh the page before adding items.');
    return window.GeightPurchasing.addItems(items);
  }

  function showPageStatus(root, message, type = 'success') {
    const node = root.querySelector('[data-sl-status]');
    if (!node) return;
    node.textContent = message;
    node.className = `geight-sl-status geight-sl-status--${type}`;
    node.hidden = false;
    window.clearTimeout(node._hideTimer);
    node._hideTimer = window.setTimeout(() => { node.hidden = true; }, 4500);
  }

  function initSavedListsPage(root) {
    const nav = root.querySelector('[data-sl-list-nav]');
    if (!nav) return;

    const panel = root.querySelector('[data-sl-list-panel]');
    const loading = root.querySelector('[data-sl-initial-loading]');
    const itemsNode = root.querySelector('[data-sl-items]');
const searchInput = root.querySelector('[data-sl-search]');
const sortSelect = root.querySelector('[data-sl-sort]');
const loadMoreButton = root.querySelector('[data-sl-load-more]');
const selectVisibleCheckbox = root.querySelector('[data-sl-select-visible]');
const addSelectedButton = root.querySelector('[data-sl-add-selected]');
const deleteSelectedButton = root.querySelector('[data-sl-delete-selected]');

const state = {
  lists: [],
  activeList: null,
  items: [],
  productData: new Map(),
  selectedIds: new Set(),
  renderLimit: 40,
};

// Export the complete active list, including rows beyond Load More.
let exportBusy = false;
let itemSavesInFlight = 0;
let listLoadsInFlight = 0;
let excelWriterPromise = null;
let exportObjectUrls = [];
const exportButton = root.querySelector('[data-sl-export-excel]');
const exportDownloads = root.querySelector('[data-sl-export-downloads]');

function getExcelWriter() {
  if (window.XLSX?.utils && window.XLSX?.write) return Promise.resolve(window.XLSX);
  if (excelWriterPromise) return excelWriterPromise;
  excelWriterPromise = new Promise((resolve, reject) => {
    const source = root.dataset.xlsxSrc;
    if (!source) { reject(new Error('Excel reader asset is missing from the Saved Lists section.')); return; }
    const script = document.createElement('script');
    script.src = source;
    script.async = true;
    const timer = setTimeout(() => finish(new Error('Excel could not load. Please try Download Excel again.')), 20000);
    let finished = false;
    function finish(error) {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      if (error) { script.remove(); reject(error); }
      else resolve(window.XLSX);
    }
    script.onload = () => finish(window.XLSX?.utils && window.XLSX?.write ? null : new Error('Excel reader is unavailable.'));
    script.onerror = () => finish(new Error('Excel reader could not load. Check the XLSX asset in your theme.'));
    document.head.appendChild(script);
  }).catch(error => { excelWriterPromise = null; throw error; });
  return excelWriterPromise;
}

function clearExportDownloads() {
  exportObjectUrls.forEach(url => URL.revokeObjectURL(url));
  exportObjectUrls = [];
  exportDownloads.replaceChildren();
  exportDownloads.hidden = true;
}

if (exportButton && exportDownloads) {
  exportButton.addEventListener('click', async () => {
    if (exportBusy) return;
    if (listLoadsInFlight || itemSavesInFlight || itemsNode.querySelector('.geight-sl-loading')) {
      showPageStatus(root, 'Please wait for the list to finish loading or saving, then click Download Excel again.', 'error');
      return;
    }
    if (!state.activeList || !state.items.length) {
      showPageStatus(root, 'This list is empty. Add some products first.', 'error');
      return;
    }

    const listId = String(state.activeList.id);
    const listName = String(state.activeList.name || 'Saved List');
    const snapshot = state.items.map(item => ({...item}));
    const byId = new Map(snapshot.map(item => [String(item.id), item]));
    itemsNode.querySelectorAll('[data-item-id]').forEach(row => {
      const item = byId.get(String(row.dataset.itemId));
      if (!item) return;
      const input = row.querySelector('[data-item-quantity]');
      if (input) item.quantity = input.value;
      // Use the saved variant ID. An HTML select can show the first variant if
      // the saved variant was removed; never silently export that replacement.
    });

    exportBusy = true;
    exportButton.disabled = true;
    exportButton.textContent = 'Preparing Excel…';
    clearExportDownloads();
    try {
      if (snapshot.some(item => item.wishlist_id && String(item.wishlist_id) !== listId)) {
        throw new Error('The list is still switching. Open your list again before exporting.');
      }
      const products = new Map();
      const handles = [...new Set(snapshot.map(item => item.product_handle).filter(Boolean))];
      let nextHandle = 0;
      await Promise.all(Array.from({length: Math.min(4, handles.length)}, async () => {
        while (nextHandle < handles.length) {
          const handle = handles[nextHandle++];
          let timer;
          try {
            const product = await Promise.race([
              loadProduct(handle),
              new Promise(resolve => { timer = setTimeout(() => resolve(null), 15000); })
            ]);
            products.set(handle, product);
          } finally { clearTimeout(timer); }
        }
      }));

      let savedSkuCount = 0;
      const errors = [];
      const rows = snapshot.map((item, index) => {
        const variant = products.get(item.product_handle)?.variants?.find(v => String(v.id) === numericVariantId(item.variant_id));
        if (!variant) savedSkuCount++;
        const sku = String(variant ? (variant.sku || '') : (item.sku || '')).trim();
        const rawQty = String(item.quantity ?? '').trim();
        const quantity = Number(rawQty);
        const description = item.product_title || 'Item ' + (index + 1);
        if (!sku || sku.length > 200 || /[\u0000-\u001f\u007f]/.test(sku)) errors.push(description + ': missing or invalid SKU');
        if (!/^\d+$/.test(rawQty) || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 99999) errors.push(description + ': invalid quantity');
        return [sku, quantity];
      });
      if (errors.length) throw new Error('Nothing exported. Please correct: ' + errors.slice(0, 5).join('; ') + (errors.length > 5 ? '; plus ' + (errors.length - 5) + ' more issues.' : '.'));

      const XLSX = await getExcelWriter();
      if (String(state.activeList?.id) !== listId || listLoadsInFlight) throw new Error('You switched lists. Click Download Excel on the list you want.');
      const safeName = listName.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'Saved-List';
      const date = new Date().toISOString().slice(0, 10);
      const parts = Math.ceil(rows.length / 200);
      const links = [];
      for (let part = 0; part < parts; part++) {
        const values = [['SKU', 'Quantity'], ...rows.slice(part * 200, (part + 1) * 200)];
        const sheet = XLSX.utils.aoa_to_sheet(values);
        for (let r = 1; r < values.length; r++) {
          sheet[XLSX.utils.encode_cell({r, c:0})] = {t:'s', v:values[r][0], z:'@'};
        }
        sheet['!cols'] = [{wch:32}, {wch:12}];
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Quick Order');
        const bytes = XLSX.write(book, {bookType:'xlsx', type:'array', compression:true});
        const url = URL.createObjectURL(new Blob([bytes], {type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}));
        exportObjectUrls.push(url);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'GEIGHT-' + safeName + '-' + date + (parts > 1 ? '-part-' + (part + 1) : '') + '.xlsx';
        link.className = 'geight-sl-button';
        link.textContent = parts > 1 ? 'Download part ' + (part + 1) + ' (' + (values.length - 1) + ' items)' : 'Download Excel (' + rows.length + ' items)';
        links.push(link);
      }
      const caption = document.createElement('p');
      caption.textContent = listName + ': ' + rows.length + ' items. ' + (parts > 1 ? 'Download each file and upload them to Quick Order one at a time.' : 'If your download did not start, use the button below.') + (savedSkuCount ? ' ' + savedSkuCount + ' items use their saved SKU; verify them in Quick Order.' : '');
      exportDownloads.append(caption, ...links);
      exportDownloads.hidden = false;
      showPageStatus(root, 'Excel ready: ' + rows.length + ' items, SKU and Quantity only.' + (savedSkuCount ? ' ' + savedSkuCount + ' items use their saved SKU because current product details were unavailable; verify them in Quick Order.' : ''));
      if (links.length === 1) links[0].click();
    } catch (error) {
      clearExportDownloads();
      showPageStatus(root, error.message || 'Could not export this list. Please retry.', 'error');
      window.clearTimeout(root.querySelector('[data-sl-status]')?._hideTimer);
    } finally {
      exportBusy = false;
      exportButton.disabled = false;
      exportButton.textContent = 'Download Excel';
    }
  });
}


function updateSelectionControls() {
  const visibleCheckboxes = Array.from(
    itemsNode.querySelectorAll('[data-item-select]')
  );

  const selectedVisibleCount = visibleCheckboxes.filter(
    (checkbox) => checkbox.checked
  ).length;

  const selectedCount = state.selectedIds.size;

  selectVisibleCheckbox.checked =
    visibleCheckboxes.length > 0 &&
    selectedVisibleCount === visibleCheckboxes.length;

  selectVisibleCheckbox.indeterminate =
    selectedVisibleCount > 0 &&
    selectedVisibleCount < visibleCheckboxes.length;

  selectVisibleCheckbox.disabled = visibleCheckboxes.length === 0;
  addSelectedButton.disabled = selectedCount === 0;
  deleteSelectedButton.disabled = selectedCount === 0;

  addSelectedButton.textContent = selectedCount
    ? `Add Selected to Cart (${selectedCount})`
    : 'Add Selected to Cart';

  deleteSelectedButton.textContent = selectedCount
    ? `Delete Selected (${selectedCount})`
    : 'Delete Selected';
}

    const currentFilteredItems = () => {
      const query = String(searchInput.value || '').trim().toLowerCase();
      const output = state.items.filter((item) => {
        if (!query) return true;
        return `${item.product_title || ''} ${item.variant_title || ''} ${item.sku || ''}`.toLowerCase().includes(query);
      });

      output.sort((a, b) => {
        if (sortSelect.value === 'title') return String(a.product_title || '').localeCompare(String(b.product_title || ''));
        if (sortSelect.value === 'sku') return String(a.sku || '').localeCompare(String(b.sku || ''));
        if (sortSelect.value === 'quantity') return Number(b.quantity || 0) - Number(a.quantity || 0);
        return String(b.created_at || '').localeCompare(String(a.created_at || ''));
      });
      return output;
    };

    function renderNav() {
      nav.innerHTML = state.lists.map((list) => `
        <li>
          <button class="geight-sl-list-button ${state.activeList?.id === list.id ? 'is-active' : ''}" type="button" data-list-id="${escapeHtml(list.id)}">
            <span class="geight-sl-list-name">${escapeHtml(list.name)}</span>
            <span class="geight-sl-count">${Number(list.item_count || 0)}</span>
          </button>
        </li>`).join('');
    }

    let renderVersion = 0, listVersion = 0;
    async function renderItems() {
      const version = ++renderVersion;
      const filtered = currentFilteredItems();
      const visible = filtered.slice(0, state.renderLimit);
      loadMoreButton.hidden = visible.length >= filtered.length;
if (!visible.length) {
  itemsNode.innerHTML = `<div class="geight-sl-empty">${state.items.length ? 'No items match your search.' : 'This list is empty. Browse products and choose “Save to List”.'}</div>`;

  updateSelectionControls();
  return;
}

      itemsNode.innerHTML = '<div class="geight-sl-loading">Loading current product information…</div>';
      const products = await Promise.all(visible.map((item) => loadProduct(item.product_handle)));
      if (version !== renderVersion) return;
      products.forEach((product, index) => state.productData.set(visible[index].id, product));

      itemsNode.innerHTML = visible.map((item) => {
        const product = state.productData.get(item.id);
        const variants = product?.variants || [];
        const selectedId = numericVariantId(item.variant_id);
        const selectedVariant = variants.find((variant) => String(variant.id) === selectedId);
        const image = selectedVariant?.featured_image?.src || item.image_url || product?.featured_image || '';
        const title = product?.title || item.product_title || 'Unavailable product';
        const sku = selectedVariant?.sku || item.sku || '';
        const price = selectedVariant ? formatMoney(selectedVariant.price) : '';
        const productUrl = item.product_handle ? `${ROOT}products/${encodeURIComponent(item.product_handle)}` : '#';
        const variantOptions = variants.length
          ? (selectedVariant ? '' : '<option value="" selected disabled>Saved variant unavailable</option>') + variants.map((variant) => `<option value="${variant.id}" ${String(variant.id) === selectedId ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>${escapeHtml(variant.title)}${variant.available ? '' : ' — Sold out'}</option>`).join('')
          : `<option value="${escapeHtml(selectedId)}">${escapeHtml(item.variant_title || 'Unavailable')}</option>`;

        return `
          <article class="geight-sl-item" data-item-id="${escapeHtml(item.id)}">
          <label class="geight-sl-item-checkbox"><input type="checkbox" data-item-select aria-label="Select ${escapeHtml(title)}" ${state.selectedIds.has(String(item.id)) ? 'checked' : ''}></label>
            ${image ? `<img class="geight-sl-item-image" src="${escapeHtml(image)}" alt="" loading="lazy">` : '<div class="geight-sl-item-image"></div>'}
            <div>
              <a class="geight-sl-item-title" href="${escapeHtml(productUrl)}">${escapeHtml(title)}</a>
              <div class="geight-sl-item-meta">${sku ? `SKU: ${escapeHtml(sku)}` : ''}${price ? ` · ${escapeHtml(price)}` : ''}</div>
            </div>
            <div class="geight-sl-item-variant"><select class="geight-sl-select" data-item-variant>${variantOptions}</select></div>
            <div class="geight-sl-item-qty"><input class="geight-sl-input" data-item-quantity type="number" min="1" max="9999" value="${clampQuantity(item.quantity)}" aria-label="Quantity"></div>
            <div class="geight-sl-item-note"><input class="geight-sl-input" data-item-note maxlength="1000" value="${escapeHtml(item.note || '')}" placeholder="Buyer note"></div>
            <div class="geight-sl-item-actions">
              <button class="geight-sl-button geight-sl-button--primary" type="button" data-item-cart>Add to Cart</button>
              <button class="geight-sl-button geight-sl-button--danger" type="button" data-item-remove>Remove</button>
            </div>
          </article>`;
   }).join('');

      updateSelectionControls();
    }

async function openList(listId) {
  const version = ++listVersion;
  ++renderVersion;
  const list = state.lists.find((entry) => entry.id === listId);
  if (!list) return;

  if (state.activeList?.id !== list.id) {
    state.selectedIds.clear();
  }

  listLoadsInFlight++;
  state.activeList = list;
      state.renderLimit = 40;
      renderNav();
      panel.hidden = false;
      root.querySelector('[data-sl-active-name]').textContent = list.name;
      root.querySelector('[data-sl-active-count]').textContent = `${Number(list.item_count || 0)} items`;
      root.querySelector('[data-sl-delete]').disabled = Number(list.is_default) === 1;
      itemsNode.innerHTML = '<div class="geight-sl-loading">Loading items…</div>';
      try {
        const data = await api(`/lists/${encodeURIComponent(list.id)}/items`);
if (version !== listVersion) return;
state.items = data.items || [];

const existingItemIds = new Set(
  state.items.map((item) => String(item.id))
);

state.selectedIds = new Set(
  Array.from(state.selectedIds).filter((itemId) =>
    existingItemIds.has(itemId)
  )
);

await renderItems();
      } catch (error) {
        if (version !== listVersion) return;
        itemsNode.innerHTML = `<div class="geight-sl-error">${escapeHtml(error.message)}</div>`;
      } finally { listLoadsInFlight--; }
    }

    async function refreshLists(preferredId) {
      state.lists = await loadLists(true);
      const nextId = preferredId || state.activeList?.id || state.lists[0]?.id;
      renderNav();
      if (nextId) await openList(nextId);
      else itemsNode.innerHTML = '<div class="geight-sl-empty">Create your first Saved List.</div>';
    }

    nav.addEventListener('click', (event) => {
      if (itemSavesInFlight || exportBusy) return;
      const button = event.target.closest('[data-list-id]');
      if (button) openList(button.dataset.listId);
    });

    root.querySelector('[data-sl-create-list]')?.addEventListener('click', async () => {
      const name = window.prompt('Name your new Saved List:');
      if (!name?.trim()) return;
      try {
        const data = await api('/lists', { method: 'POST', body: { name: name.trim() } });
        showPageStatus(root, `Created “${data.list.name}”.`);
        await refreshLists(data.list.id);
      } catch (error) { showPageStatus(root, error.message, 'error'); }
    });

    root.querySelector('[data-sl-rename]').addEventListener('click', async () => {
      if (!state.activeList) return;
      const name = window.prompt('Rename this Saved List:', state.activeList.name);
      if (!name?.trim() || name.trim() === state.activeList.name) return;
      try {
        await api(`/lists/${encodeURIComponent(state.activeList.id)}`, { method: 'PATCH', body: { name: name.trim() } });
        showPageStatus(root, 'List renamed.');
        await refreshLists(state.activeList.id);
      } catch (error) { showPageStatus(root, error.message, 'error'); }
    });

    root.querySelector('[data-sl-delete]').addEventListener('click', async () => {
      if (!state.activeList || Number(state.activeList.is_default) === 1) return;
      if (!window.confirm(`Delete “${state.activeList.name}” and all items inside it?`)) return;
      try {
        await api(`/lists/${encodeURIComponent(state.activeList.id)}`, { method: 'DELETE' });
        showPageStatus(root, 'List deleted.');
        state.activeList = null;
        await refreshLists();
      } catch (error) { showPageStatus(root, error.message, 'error'); }
    });

    root.querySelector('[data-sl-add-all]').addEventListener('click', async (event) => {
  if (!state.items.length) return;

  const button = event.currentTarget;

  /*
   * Read the latest quantities and notes currently visible
   * on the page before adding everything.
   */
  root.querySelectorAll('[data-item-id]').forEach((row) => {
    const item = state.items.find(
      (entry) => entry.id === row.dataset.itemId
    );

    if (!item) return;

    item.quantity = clampQuantity(
      row.querySelector('[data-item-quantity]')?.value
    );

    item.note =
      row.querySelector('[data-item-note]')?.value || '';
  });

  button.disabled = true;
  button.textContent = 'Adding…';

  try {
    const result = await addItemsToCart(state.items);

    if (result.failedItems.length) {
      showPageStatus(
        root,
        `${result.addedLines} items added. ${result.failedItems.length} items skipped: ${result.failedItems.slice(0, 3).map(x => x.message).join("; ")}`,
        'error'
      );
    } else {
      showPageStatus(
        root,
        `${result.addedLines} items were added to cart.`
      );
    }
  } catch (error) {
    showPageStatus(root, error.message, 'error');
  } finally {
    button.disabled = false;
    button.textContent = 'Add All to Cart';
  }
});
selectVisibleCheckbox.addEventListener('change', () => {
  const shouldSelect = selectVisibleCheckbox.checked;

  itemsNode.querySelectorAll('[data-item-select]').forEach((checkbox) => {
    const row = checkbox.closest('[data-item-id]');
    if (!row) return;

    checkbox.checked = shouldSelect;

    if (shouldSelect) {
      state.selectedIds.add(String(row.dataset.itemId));
    } else {
      state.selectedIds.delete(String(row.dataset.itemId));
    }
  });

  updateSelectionControls();
});
deleteSelectedButton.addEventListener('click', async () => {
  const selectedIds = Array.from(state.selectedIds);

  if (!selectedIds.length || !state.activeList) return;

  const confirmed = window.confirm(
    `Delete ${selectedIds.length} selected items from this list?`
  );

  if (!confirmed) return;

  const activeListId = state.activeList.id;
  let deletedCount = 0;
  let failedCount = 0;

  deleteSelectedButton.disabled = true;
  addSelectedButton.disabled = true;
  deleteSelectedButton.textContent = 'Deleting…';

  try {
    /*
     * Delete five items at a time to avoid sending
     * too many simultaneous requests to the Worker.
     */
    for (let start = 0; start < selectedIds.length; start += 5) {
      const batch = selectedIds.slice(start, start + 5);

      const results = await Promise.allSettled(
        batch.map((itemId) =>
          api(
            `/lists/${encodeURIComponent(activeListId)}/items/${encodeURIComponent(itemId)}`,
            { method: 'DELETE' }
          )
        )
      );

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          deletedCount += 1;
        } else {
          failedCount += 1;
        }
      });
    }

    state.selectedIds.clear();
    await refreshLists(activeListId);

    if (failedCount) {
      showPageStatus(
        root,
        `${deletedCount} items deleted. ${failedCount} items could not be deleted.`,
        'error'
      );
    } else {
      showPageStatus(
        root,
        `${deletedCount} selected items were deleted.`
      );
    }
  } catch (error) {
    showPageStatus(root, error.message, 'error');
  } finally {
    updateSelectionControls();
  }
});
addSelectedButton.addEventListener('click', async () => {
  const selectedItems = state.items.filter((item) =>
    state.selectedIds.has(String(item.id))
  );

  if (!selectedItems.length) return;

  /*
   * Read the latest quantity currently entered by the customer.
   */
  itemsNode.querySelectorAll('[data-item-id]').forEach((row) => {
    const item = selectedItems.find(
      (entry) => String(entry.id) === String(row.dataset.itemId)
    );

    if (!item) return;

    item.quantity = clampQuantity(
      row.querySelector('[data-item-quantity]')?.value
    );

    item.note =
      row.querySelector('[data-item-note]')?.value || '';
  });

  addSelectedButton.disabled = true;
  addSelectedButton.textContent = 'Adding…';

  try {
    const result = await addItemsToCart(selectedItems);
    const failedCount = result?.failedItems?.length || 0;
    const addedCount = result?.addedLines || selectedItems.length;

    if (failedCount) {
      showPageStatus(
        root,
        `${addedCount} selected items added. ${failedCount} items skipped: ${result.failedItems.slice(0, 3).map(x => x.message).join("; ")}`,
        'error'
      );
    } else {
      showPageStatus(
        root,
        `${addedCount} selected items were added to cart.`
      );
    }
  } catch (error) {
    showPageStatus(root, error.message, 'error');
  } finally {
    updateSelectionControls();
  }
});
    searchInput.addEventListener('input', () => { state.renderLimit = 40; renderItems(); });
    sortSelect.addEventListener('change', () => { state.renderLimit = 40; renderItems(); });
    loadMoreButton.addEventListener('click', () => { state.renderLimit += 40; renderItems(); });

    itemsNode.addEventListener('click', async (event) => {
      const row = event.target.closest('[data-item-id]');
      if (!row || !state.activeList) return;
      const item = state.items.find((entry) => entry.id === row.dataset.itemId);
      if (!item) return;

      if (event.target.closest('[data-item-remove]')) {
        if (!window.confirm('Remove this item from the list?')) return;
        try {
          await api(`/lists/${encodeURIComponent(state.activeList.id)}/items/${encodeURIComponent(item.id)}`, { method: 'DELETE' });
          await refreshLists(state.activeList.id);
        } catch (error) { showPageStatus(root, error.message, 'error'); }
      }

      if (event.target.closest('[data-item-cart]')) {
        const quantity = clampQuantity(row.querySelector('[data-item-quantity]').value);
        try {
          await addItemsToCart([{ variant_id: row.querySelector('[data-item-variant]').value, quantity, product_handle: item.product_handle }]);
          showPageStatus(root, 'Item added to cart.');
        } catch (error) { showPageStatus(root, error.message, 'error'); }
      }
    });

    itemsNode.addEventListener('change', async (event) => {
      const row = event.target.closest('[data-item-id]');
      if (!row || !state.activeList) return;
const item = state.items.find((entry) => entry.id === row.dataset.itemId);
if (!item) return;

if (event.target.matches('[data-item-select]')) {
  if (event.target.checked) {
    state.selectedIds.add(String(item.id));
  } else {
    state.selectedIds.delete(String(item.id));
  }

  updateSelectionControls();
  return;
}

itemSavesInFlight++;
try {
        if (event.target.matches('[data-item-quantity], [data-item-note]')) {
          const updatedQuantity = clampQuantity(row.querySelector('[data-item-quantity]').value);
          const updatedNote = row.querySelector('[data-item-note]').value;
          await api(`/lists/${encodeURIComponent(state.activeList.id)}/items/${encodeURIComponent(item.id)}`, {
            method: 'PATCH',
            body: {
              quantity: updatedQuantity,
              note: updatedNote,
            },
          });
          item.quantity = updatedQuantity;
          item.note = updatedNote;
          showPageStatus(root, 'Item updated.');
        }

        if (event.target.matches('[data-item-variant]')) {
          const product = state.productData.get(item.id);
          const variant = product?.variants?.find((entry) => String(entry.id) === event.target.value);
          if (!variant || String(variant.id) === numericVariantId(item.variant_id)) return;
          const payload = {
            product_id: product.id,
            variant_id: String(variant.id),
            product_handle: item.product_handle,
            product_title: product.title,
            variant_title: variant.title,
            image_url: variant.featured_image?.src || product.featured_image || item.image_url,
            sku: variant.sku || '',
            quantity: clampQuantity(row.querySelector('[data-item-quantity]').value),
            note: row.querySelector('[data-item-note]').value,
          };
          await api(`/lists/${encodeURIComponent(state.activeList.id)}/items`, { method: 'POST', body: payload });
          await api(`/lists/${encodeURIComponent(state.activeList.id)}/items/${encodeURIComponent(item.id)}`, { method: 'DELETE' });
          showPageStatus(root, 'Variant changed.');
          await refreshLists(state.activeList.id);
        }
      } catch (error) { showPageStatus(root, error.message, 'error'); }
      finally { itemSavesInFlight--; }
    });

    loadLists(true)
      .then((lists) => {
        state.lists = lists;
        loading.hidden = true;
        panel.hidden = false;
        renderNav();
        if (lists[0]) return openList(lists[0].id);
        itemsNode.innerHTML = '<div class="geight-sl-empty">Create your first Saved List.</div>';
      })
      .catch((error) => {
        loading.className = 'geight-sl-error';
        loading.textContent = error.message;
      });
  }

  function ensureSaveModal() {
    let modal = document.querySelector('[data-geight-save-modal]');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'geight-sl-modal';
    modal.hidden = true;
    modal.dataset.geightSaveModal = '';
    modal.innerHTML = `
      <div class="geight-sl-dialog" role="dialog" aria-modal="true" aria-labelledby="geight-save-title">
        <div class="geight-sl-dialog-header"><h2 id="geight-save-title">Save to List</h2><button class="geight-sl-icon-button" type="button" data-save-close aria-label="Close">×</button></div>
        <div data-save-message></div>
        <div class="geight-sl-choice-list" data-save-lists></div>
        <label for="geight-new-list-name"><strong>Or create a new list</strong></label>
        <input id="geight-new-list-name" class="geight-sl-input" maxlength="80" placeholder="Example: Spring Collection" data-save-new-name>
        <div class="geight-sl-dialog-actions"><button class="geight-sl-button" type="button" data-save-close>Cancel</button><button class="geight-sl-button geight-sl-button--primary" type="button" data-save-submit>Save Item</button></div>
      </div>`;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.closest('[data-save-close]')) closeSaveModal();
    });
    modal.querySelector('[data-save-submit]').addEventListener('click', submitSaveModal);
    return modal;
  }

  function closeSaveModal() {
    const modal = document.querySelector('[data-geight-save-modal]');
    if (modal) modal.hidden = true;
    activeSavePayload = null;
  }

  async function payloadFromTrigger(trigger) {
  const productCard = trigger.closest('product-card');

  const controls =
  trigger.closest('[data-geight-product-controls]') ||
  productCard?.querySelector('[data-geight-product-controls]');

    let variantId = trigger.dataset.variantId || '';
    let quantity = 1;
    let variantTitle = '';
    let sku = '';
    let image = trigger.dataset.productImage || controls?.dataset.productImage || '';

    if (controls) {
      const select = controls.querySelector('[data-geight-variant]');
      const option = select?.selectedOptions?.[0];
      variantId = select?.value;
      quantity = clampQuantity(controls.querySelector('[data-geight-quantity]')?.value);
      variantTitle = option?.dataset.title || option?.textContent?.trim() || '';
      sku = option?.dataset.sku || '';
      image = option?.dataset.image || image;
    } else {
      const formId = trigger.dataset.productFormId;
      const form =
      (formId ? document.getElementById(formId) : null) ||
      trigger.closest('form') ||
      productCard?.querySelector('form');
      variantId =
      form?.querySelector('[name="id"]')?.value ||
      variantId;
      quantity = clampQuantity(form?.querySelector('[name="quantity"]')?.value);
    }

    const handle = trigger.dataset.productHandle || controls?.dataset.productHandle || '';
    const product = await loadProduct(handle);
    const variant = product?.variants?.find((entry) => String(entry.id) === String(variantId));
    if (variant) {
      variantTitle = variant.title;
      sku = variant.sku || '';
      image = variant.featured_image?.src || product.featured_image || image;
    }
    if (!variantId) throw new Error('Please choose a product variant first.');

    return {
      product_id: String(product?.id || trigger.dataset.productId || controls?.dataset.productId || ''),
      variant_id: String(variantId),
      product_handle: handle,
      product_title: product?.title || trigger.dataset.productTitle || controls?.dataset.productTitle || '',
      variant_title: variantTitle,
      image_url: image,
      sku,
      quantity,
      note: '',
    };
  }

  async function openSaveModal(trigger) {
    const modal = ensureSaveModal();
    const listNode = modal.querySelector('[data-save-lists]');
    const messageNode = modal.querySelector('[data-save-message]');
    modal.hidden = false;
    listNode.innerHTML = '<div class="geight-sl-loading">Loading lists…</div>';
    messageNode.textContent = '';
    modal.querySelector('[data-save-new-name]').value = '';

    try {
      activeSavePayload = await payloadFromTrigger(trigger);
      const lists = await loadLists(true);
      listNode.innerHTML = lists.map((list, index) => `
        <label class="geight-sl-choice"><input type="radio" name="geight-save-list" value="${escapeHtml(list.id)}" ${index === 0 ? 'checked' : ''}><span><strong>${escapeHtml(list.name)}</strong><br><small>${Number(list.item_count || 0)} items</small></span></label>`).join('');
    } catch (error) {
      listNode.innerHTML = `<div class="geight-sl-error">${escapeHtml(error.message)}</div>`;
    }
  }

  async function submitSaveModal(event) {
    const modal = ensureSaveModal();
    const button = event.currentTarget;
    const messageNode = modal.querySelector('[data-save-message]');
    if (!activeSavePayload) return;
    button.disabled = true;
    button.textContent = 'Saving…';

    try {
      const newName = modal.querySelector('[data-save-new-name]').value.trim();
      let listId = modal.querySelector('input[name="geight-save-list"]:checked')?.value;
      if (newName) {
        const created = await api('/lists', { method: 'POST', body: { name: newName } });
        listId = created.list.id;
      }
      if (!listId) throw new Error('Choose a list or enter a new list name.');
      await api(`/lists/${encodeURIComponent(listId)}/items`, { method: 'POST', body: activeSavePayload });
      listsCache = null;
      messageNode.className = 'geight-sl-status geight-sl-status--success';
      messageNode.textContent = 'Saved successfully.';
      window.setTimeout(closeSaveModal, 700);
    } catch (error) {
      messageNode.className = 'geight-sl-status geight-sl-status--error';
      messageNode.textContent = error.message;
    } finally {
      button.disabled = false;
      button.textContent = 'Save Item';
    }
  }

  document.addEventListener('click', async (event) => {
    const cartButton = event.target.closest('[data-geight-add-cart]');
    if (cartButton) {
      const controls = cartButton.closest('[data-geight-product-controls]');
      const variantId = controls?.querySelector('[data-geight-variant]')?.value;
      const quantity = clampQuantity(controls?.querySelector('[data-geight-quantity]')?.value);
      cartButton.disabled = true;
      const original = cartButton.textContent;
      cartButton.textContent = 'Adding…';
      try { await addItemsToCart([{ variant_id: variantId, quantity, product_handle: controls?.dataset.productHandle }]); cartButton.textContent = 'Added'; }
      catch (error) { window.alert(error.message); cartButton.textContent = original; }
      window.setTimeout(() => { cartButton.disabled = false; cartButton.textContent = original; }, 1000);
      return;
    }

    const saveButton = event.target.closest('button.geight-save-trigger');
    if (saveButton) {
      event.preventDefault();
      openSaveModal(saveButton);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-geight-saved-lists-page]').forEach(initSavedListsPage);
  });
})();
